import React, { useState } from 'react';
import { X, Eye, EyeOff, Smartphone, Loader2 } from 'lucide-react';
import { loginadmin } from '../../assets/assets';
import { Link, useNavigate } from 'react-router-dom';
import { useLogin, useVerify2FALogin } from '../../services/auth/auth';
import { useAlert } from '../../context/AlertContext';
import { useAuthStore } from '../../store/authStore';

const SignInAdmin = ({ onClose }) => {
  const { setTokens } = useAuthStore();
  const navigate = useNavigate();
  const { mutateAsync: loginUser, isPending: isLoggingIn } = useLogin();
  const { mutateAsync: verify2FALogin, isPending: isVerifying2FA } = useVerify2FALogin();
  const setAlert = useAlert();
  
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [tempEmail, setTempEmail] = useState('');
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  
  const [showPassword, setShowPassword] = useState(false);
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await loginUser(formData);
      
      if (response.requires2FA) {
        setRequires2FA(true);
        setTempEmail(response.email);
        setAlert({ type: 'info', message: 'Two-factor authentication required' });
        return;
      }

      const { accessToken, refreshToken, user } = response;
      const { role } = user;
     
      setTokens({
        accessToken,
        refreshToken,
        role,
        user
      });
      setAlert({ type: 'success', message: `Welcome back, Admin ${response.user.firstName}!` });
      navigate('/admins/dashboard');
    } catch (error) {
      const err = error.response?.data?.error || 'Login failed'; 
      setAlert({ type: 'error', message: err });
    }
  };

  const handle2FAVerify = async (e) => {
    e.preventDefault();
    if (twoFactorCode.length !== 6) {
      setAlert({ type: 'error', message: 'Please enter a 6-digit code' });
      return;
    }

    try {
      const response = await verify2FALogin({ email: tempEmail, code: twoFactorCode });
      const { accessToken, refreshToken, user } = response;
      const { role } = user;
     
      setTokens({
        accessToken,
        refreshToken,
        role,
        user
      });
      setAlert({ type: 'success', message: `Welcome back, Admin ${response.user.firstName}!` });
      navigate('/admins/dashboard');
    } catch (error) {
      setAlert({ type: 'error', message: error.response?.data?.error || '2FA verification failed' });
    }
  };

  return (
    <div className="font-['Poppins'] bg-gray-50 min-h-screen flex md:flex-row-reverse flex-col">
      {/* Image section - full width on mobile, left half on desktop */}
      <div className="w-[88%] mt-[4rem] md:mt-0 mx-auto md:w-1/2 md:max-h-screen">
        {/* Close button - visible only on mobile */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 z-10 md:hidden"
          aria-label="Close"
        >
          <Link to={"/"}>
          <X size={24} className="text-gray-800" />
          </Link>
        </button>
        
        <img
          src={loginadmin}
          alt="Codes"
          className="w-full h-64 md:h-full object-cover"
        />
      </div>
      
      {/* Form section - full width on mobile, right half on desktop */}
      <div className="md:w-1/2 px-6 py-8 md:px-16 md:py-12 md:flex md:flex-col md:justify-center">
        <h1 className="text-2xl font-bold text-[#174568] mb-6">
          {requires2FA ? 'Two-Factor Auth' : 'Admin Login'}
        </h1>
        
        {requires2FA ? (
          <form onSubmit={handle2FAVerify} className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Smartphone className="text-[#174568] w-8 h-8" />
              </div>
              <p className="text-gray-600 text-sm mb-6">
                Enter the 6-digit code from your authenticator app to complete login.
              </p>
            </div>

            <div>
              <input
                type="text"
                maxLength={6}
                value={twoFactorCode}
                onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                className="w-full p-4 border-2 border-gray-200 rounded-xl text-center tracking-[0.5em] font-mono text-2xl focus:outline-none focus:border-[#174568] transition-colors"
                required
              />
            </div>

            <button
              type="submit"
              disabled={isVerifying2FA || twoFactorCode.length !== 6}
              className="w-full bg-[#174568] hover:bg-gray-800 text-white font-medium py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
            >
              {isVerifying2FA ? <Loader2 className="animate-spin" size={20} /> : 'Verify & Log In'}
            </button>

            <button
              type="button"
              onClick={() => {
                setRequires2FA(false);
                setTwoFactorCode('');
              }}
              className="w-full text-gray-500 text-sm hover:underline"
            >
              Back to Login
            </button>
          </form>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-gray-700 mb-2">Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter email address"
                className="w-full p-3 
                border border-[#C1C7CD] 
                md:border-x-0 md:border-t-0 
                md:bg-[#F2F4F8] bg-transparent 
                rounded-md md:rounded-none 
                focus:outline-none 
                focus:border-[#174568] 
                md:focus:border-b-[#174568] md:focus:border-x-0 md:focus:border-t-0"
                required
              />
            </div>
            
            <div>
              <label htmlFor="password" className="block text-gray-700 mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter password"
                  className="w-full p-3 
                  border border-[#C1C7CD] 
                  md:border-x-0 md:border-t-0 
                  md:bg-[#F2F4F8] bg-transparent 
                  rounded-md md:rounded-none 
                  focus:outline-none 
                  focus:border-[#174568] 
                  md:focus:border-b-[#174568] md:focus:border-x-0 md:focus:border-t-0"
                  required
                />
                <button 
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            
            </div>
            
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="rememberMe"
                  name="rememberMe"
                  checked={formData.rememberMe}
                  onChange={handleChange}
                  className="w-4 h-4 text-[#174568] border-gray-300 rounded focus:ring-[#174568]"
                />
                <label htmlFor="rememberMe" className="ml-2 block text-sm text-gray-700 text-nowrap">
                  Remember me
                </label>
              </div>
              <Link to="/forgot-password" size="sm" className="text-[#001D6C] hover:underline text-sm">Forget Password?</Link>
            </div>
            
            <button
              type="submit"
              disabled={isLoggingIn}
              className="w-full bg-[#174568] hover:bg-gray-800 text-white font-medium py-3 px-4 flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
            >
              {isLoggingIn ? <Loader2 className="animate-spin" size={20} /> : 'Log In'}
            </button>
            
            <div className="text-left mt-6">
              <Link to={"/admins/activate"} className="text-[#001D6C] hover:underline">New admin? Activate your account</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default SignInAdmin;